const _excluded = ["value", "defaultValue", "disabled", "error", "fullWidth", "id", "onChange", "overrideClassName", "onClick", "prefix", "renderHelperText", "renderEndSlot", "renderInput", "renderLabel", "renderStartSlot", "size", "suffix", "type", "inputWrapperRef"],
  _excluded2 = ["error", "disabled", "helperText", "helperTextId", "inputId", "label", "labelId", "prefix", "prefixId", "suffix", "suffixId", "fullWidth", "clearTextLabel", "renderHelperText", "renderEndSlot", "renderLabel", "renderInput", "renderStartSlot", "showPasswordLabel", "size"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import * as React from "react";
import { useId } from "@react-aria/utils";
import Alert from "@bolteu/kalep-react-icons/dist/Alert";
import { cx } from "../../helpers/utils/cx";
import { mergeRefs } from "../../helpers/utils/mergeRefs";
import { renderHelperTextDefault } from "../_internal/HelperText";
const sizes = {
  sm: "h-9 px-2 bolt-font-body-s-regular gap-2",
  md: "h-12 px-3 bolt-font-body-m-regular gap-3",
  lg: "h-14 px-4 bolt-font-body-m-regular gap-3"
};
const states = {
  idle: "border-special-nulled focus-within:!border-action-primary text-primary",
  disabled: "border-neutral-secondary cursor-not-allowed text-tertiary bg-special-nulled",
  error: "border-danger-primary text-primary caret-danger-primary"
};
export const BaseTextField = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  const {
      value,
      defaultValue,
      disabled = false,
      error = false,
      fullWidth = false,
      id,
      onChange,
      overrideClassName,
      onClick: onClickProp,
      prefix,
      renderHelperText = renderHelperTextDefault,
      renderEndSlot = renderEndSlotDefault,
      renderInput = renderInputDefault,
      renderLabel = renderLabelDefault,
      renderStartSlot = noop,
      size = "md",
      suffix,
      type = "text",
      inputWrapperRef
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const generatedId = useId();
  const inputId = id ?? `kalep-text-field-${generatedId}`;
  const helperTextId = `${inputId}-helper-text`;
  const labelId = `${inputId}-label`;
  const suffixId = `${inputId}-suffix`;
  const prefixId = `${inputId}-prefix`;
  const internalRef = React.useRef();
  const renderProps = _objectSpread({
    defaultValue,
    disabled,
    error,
    fullWidth,
    id,
    onChange,
    prefix,
    ref: mergeRefs([internalRef, forwardedRef]),
    renderHelperText,
    renderEndSlot,
    renderInput,
    renderLabel,
    renderStartSlot,
    size,
    suffix,
    type,
    value,
    helperTextId,
    prefixId,
    suffixId,
    inputId,
    labelId
  }, rest);
  const handleWrapperClick = React.useCallback(function clickHandler(event) {
    if (internalRef?.current && document.activeElement !== internalRef.current) {
      internalRef.current.focus();
    }
    if (onClickProp) {
      onClickProp(event);
    }
  }, [internalRef, onClickProp]);
  return /*#__PURE__*/React.createElement("div", {
    className: cx("flex flex-col gap-2", fullWidth ? "w-full" : "w-96")
  }, renderLabel(renderProps, renderLabelDefault), /*#__PURE__*/React.createElement("div", {
    ref: inputWrapperRef,
    className: cx("flex justify-end items-center w-full", "border-solid border-2 rounded-md", "bg-neutral-secondary caret-action-primary", "ease-linear duration-100", "focus-within:bg-special-nulled", disabled ? "cursor-not-allowed" : "cursor-text", disabled && states.disabled, error && states.error, !disabled && !error && states.idle, sizes[size], overrideClassName),
    role: "presentation",
    "data-testid": "text-field-wrapper",
    onClick: handleWrapperClick
  }, prefix ? /*#__PURE__*/React.createElement("span", {
    className: cx("shrink-0", disabled ? "text-tertiary" : "text-secondary"),
    id: prefixId
  }, prefix) : null, renderStartSlot(renderProps, noop), renderInput(renderProps, renderInputDefault), renderEndSlot(renderProps, renderEndSlotDefault), suffix ? /*#__PURE__*/React.createElement("span", {
    className: cx("shrink-0", disabled ? "text-tertiary" : "text-secondary"),
    id: suffixId
  }, suffix) : null), renderHelperText(renderProps, renderHelperTextDefault));
});
function renderEndSlotDefault(_ref) {
  let {
    error,
    size
  } = _ref;
  return error ? /*#__PURE__*/React.createElement(Alert, {
    className: cx("text-danger-secondary flex-none"),
    "aria-hidden": "true",
    "data-testid": "textfield-error-icon",
    size: size === "sm" ? "sm" : "lg"
  }) : null;
}
function renderInputDefault(_ref2) {
  let {
      error = false,
      disabled,
      helperText,
      helperTextId,
      inputId,
      label,
      labelId,
      prefix,
      prefixId,
      suffix,
      suffixId,
      fullWidth: _fullWidth,
      clearTextLabel: _clearTextLabel,
      renderHelperText: _renderHelperText,
      renderEndSlot: _renderEndSlot,
      renderLabel: _renderLabel,
      renderInput: _renderInput,
      renderStartSlot: _renderStartSlot,
      showPasswordLabel: _showPasswordLabel,
      size: _size
    } = _ref2,
    inputProps = _objectWithoutProperties(_ref2, _excluded2);
  return /*#__PURE__*/React.createElement("input", _extends({}, inputProps, {
    disabled: disabled,
    "aria-describedby": helperText ? helperTextId : undefined,
    "aria-invalid": error,
    "aria-labelledby": label || prefix || suffix ? `${label ? labelId : ""} ${prefix ? prefixId : ""} ${suffix ? suffixId : ""}` : undefined,
    className: cx("outline-none border-0 border-solid m-0 p-0", "[color:inherit] text-size-inherit",
    // [color:inherit] is intentional to block twMerge from removing it.
    "w-full h-full bg-transparent disabled:cursor-not-allowed input-clear-button:hidden", disabled ? "placeholder-tertiary" : "placeholder-secondary"),
    id: inputId
  }));
}
function renderLabelDefault(_ref3) {
  let {
    inputId,
    label,
    labelId,
    required,
    disabled
  } = _ref3;
  if (!label) {
    return null;
  }
  return /*#__PURE__*/React.createElement("label", {
    id: labelId,
    htmlFor: inputId,
    className: cx("w-fit bolt-font-body-s-accent", disabled ? " text-tertiary" : "text-primary", required && "after:text-danger-primary after:content-['_*']")
  }, label);
}
function noop() {
  return null;
}